import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { TRACING_DEFAULTS, addTracingExtensions, spanToJSON, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, getActiveSpan, getCurrentHub, startIdleTransaction, getActiveTransaction, getClient, getCurrentScope } from '@sentry/core';
import { logger, browserPerformanceTimeOrigin, addHistoryInstrumentationHandler, propagationContextFromHeaders, getDomElement } from '@sentry/utils';
import { DEBUG_BUILD } from '../common/debug-build.js';
import { registerBackgroundTabDetection } from './backgroundtab.js';
import { addPerformanceInstrumentationHandler } from './instrument.js';
import { startTrackingWebVitals, startTrackingINP, startTrackingLongTasks, startTrackingInteractions, addPerformanceEntries } from './metrics/index.js';
import { defaultRequestInstrumentationOptions, instrumentOutgoingRequests } from './request.js';
import { WINDOW } from './types.js';
var BROWSER_TRACING_INTEGRATION_ID = 'BrowserTracing';
/** Options for Browser Tracing integration */

var DEFAULT_BROWSER_TRACING_OPTIONS = _objectSpread(_objectSpread({}, TRACING_DEFAULTS), {}, {
  instrumentNavigation: true,
  instrumentPageLoad: true,
  markBackgroundSpan: true,
  enableLongTask: true,
  enableInp: false,
  interactionsSampleRate: 1,
  _experiments: {}
}, defaultRequestInstrumentationOptions);
/**
 * The Browser Tracing integration automatically instruments browser pageload/navigation
 * actions as transactions, and captures requests, metrics and errors as spans.
 *
 * The integration can be configured with a variety of options, and can be extended to use
 * any routing library. This integration uses {@see IdleTransaction} to create transactions.
 *
 * We explicitly export the proper type here, as this has to be extended in some cases.
 */


var browserTracingIntegration = function browserTracingIntegration() {
  var _options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _hasSetTracePropagationTargets = DEBUG_BUILD ? !!( // eslint-disable-next-line deprecation/deprecation
  _options.tracePropagationTargets || _options.tracingOrigins) : false;

  addTracingExtensions(); // TODO (v8): remove this block after tracingOrigins is removed
  // Set tracePropagationTargets to tracingOrigins if specified by the user
  // In case both are specified, tracePropagationTargets takes precedence
  // eslint-disable-next-line deprecation/deprecation

  if (!_options.tracePropagationTargets && _options.tracingOrigins) {
    // eslint-disable-next-line deprecation/deprecation
    _options.tracePropagationTargets = _options.tracingOrigins;
  }

  var options = _objectSpread(_objectSpread({}, DEFAULT_BROWSER_TRACING_OPTIONS), _options);

  var _collectWebVitals = startTrackingWebVitals();
  /** Stores a mapping of interactionIds from PerformanceEventTimings to the origin interaction path */


  var interactionIdToRouteNameMapping = {};

  if (options.enableInp) {
    startTrackingINP(interactionIdToRouteNameMapping, options.interactionsSampleRate);
  }

  if (options.enableLongTask) {
    startTrackingLongTasks();
  }

  if (options._experiments.enableInteractions) {
    startTrackingInteractions();
  }

  var latestRoute = {
    name: undefined,
    context: undefined
  };
  /** Create routing idle transaction. */

  function _createRouteTransaction(context) {
    // eslint-disable-next-line deprecation/deprecation
    var hub = getCurrentHub();
    var beforeStartSpan = options.beforeStartSpan,
        idleTimeout = options.idleTimeout,
        finalTimeout = options.finalTimeout,
        heartbeatInterval = options.heartbeatInterval;
    var isPageloadTransaction = context.op === 'pageload';
    var expandedContext;

    if (isPageloadTransaction) {
      var sentryTrace = isPageloadTransaction ? getMetaContent('sentry-trace') : '';
      var baggage = isPageloadTransaction ? getMetaContent('baggage') : undefined;

      var _propagationContextFr = propagationContextFromHeaders(sentryTrace, baggage),
          traceId = _propagationContextFr.traceId,
          dsc = _propagationContextFr.dsc,
          parentSpanId = _propagationContextFr.parentSpanId,
          sampled = _propagationContextFr.sampled;

      expandedContext = _objectSpread(_objectSpread({
        traceId: traceId,
        parentSpanId: parentSpanId,
        parentSampled: sampled
      }, context), {}, {
        metadata: _objectSpread(_objectSpread({}, context.metadata), {}, {
          dynamicSamplingContext: dsc
        }),
        trimEnd: true
      });
    } else {
      expandedContext = _objectSpread({
        trimEnd: true
      }, context);
    }

    var finalContext = beforeStartSpan ? beforeStartSpan(expandedContext) : expandedContext; // If `beforeStartSpan` set a custom name, record that fact
    // eslint-disable-next-line deprecation/deprecation

    finalContext.metadata = finalContext.name !== expandedContext.name ? // eslint-disable-next-line deprecation/deprecation
    _objectSpread(_objectSpread({}, finalContext.metadata), {}, {
      source: 'custom'
    }) : // eslint-disable-next-line deprecation/deprecation
    finalContext.metadata;
    latestRoute.name = finalContext.name;
    latestRoute.context = finalContext;

    if (finalContext.sampled === false) {
      DEBUG_BUILD && logger.log("[Tracing] Will not send ".concat(finalContext.op, " transaction because of beforeNavigate."));
    }

    DEBUG_BUILD && logger.log("[Tracing] Starting ".concat(finalContext.op, " transaction on scope"));
    var location = WINDOW.location;
    var idleTransaction = startIdleTransaction(hub, finalContext, idleTimeout, finalTimeout, true, {
      location: location
    }, // for use in the tracesSampler
    heartbeatInterval, isPageloadTransaction // should wait for finish signal if it's a pageload transaction
    );

    if (isPageloadTransaction && WINDOW.document) {
      WINDOW.document.addEventListener('readystatechange', function () {
        if (['interactive', 'complete'].includes(WINDOW.document.readyState)) {
          idleTransaction.sendAutoFinishSignal();
        }
      });

      if (['interactive', 'complete'].includes(WINDOW.document.readyState)) {
        idleTransaction.sendAutoFinishSignal();
      }
    }

    idleTransaction.registerBeforeFinishCallback(function (transaction) {
      _collectWebVitals();

      addPerformanceEntries(transaction);
    });
    return idleTransaction;
  }

  return {
    name: BROWSER_TRACING_INTEGRATION_ID,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setupOnce: function setupOnce() {},
    afterAllSetup: function afterAllSetup(client) {
      var clientOptions = client.getOptions();
      var markBackgroundSpan = options.markBackgroundSpan,
          traceFetch = options.traceFetch,
          traceXHR = options.traceXHR,
          shouldCreateSpanForRequest = options.shouldCreateSpanForRequest,
          enableHTTPTimings = options.enableHTTPTimings,
          _experiments = options._experiments;
      var clientOptionsTracePropagationTargets = clientOptions && clientOptions.tracePropagationTargets; // There are three ways to configure tracePropagationTargets:
      // 1. via top level client option `tracePropagationTargets`
      // 2. via BrowserTracing option `tracePropagationTargets`
      // 3. via BrowserTracing option `tracingOrigins` (deprecated)
      //
      // To avoid confusion, favour top level client option `tracePropagationTargets`, and fallback to
      // BrowserTracing option `tracePropagationTargets` and then `tracingOrigins` (deprecated).
      // This is done as it minimizes bundle size (we don't have to have undefined checks).
      //
      // If both 1 and either one of 2 or 3 are set (from above), we log out a warning.
      // eslint-disable-next-line deprecation/deprecation

      var tracePropagationTargets = clientOptionsTracePropagationTargets || options.tracePropagationTargets;

      if (DEBUG_BUILD && _hasSetTracePropagationTargets && clientOptionsTracePropagationTargets) {
        logger.warn('[Tracing] The `tracePropagationTargets` option was set in the BrowserTracing integration and top level `Sentry.init`. The top level `Sentry.init` value is being used.');
      }

      var activeSpan;
      var startingUrl = WINDOW.location && WINDOW.location.href;

      if (client.on) {
        client.on('startNavigationSpan', function (context) {
          if (activeSpan) {
            DEBUG_BUILD && logger.log("[Tracing] Finishing current transaction with op: ".concat(spanToJSON(activeSpan).op)); // If there's an open transaction on the scope, we need to finish it before creating an new one.

            activeSpan.end();
          }

          activeSpan = _createRouteTransaction(_objectSpread({
            op: 'navigation'
          }, context));
        });
        client.on('startPageLoadSpan', function (context) {
          if (activeSpan) {
            DEBUG_BUILD && logger.log("[Tracing] Finishing current transaction with op: ".concat(spanToJSON(activeSpan).op)); // If there's an open transaction on the scope, we need to finish it before creating an new one.

            activeSpan.end();
          }

          activeSpan = _createRouteTransaction(_objectSpread({
            op: 'pageload'
          }, context));
        });
      }

      if (options.instrumentPageLoad && client.emit && WINDOW.location) {
        var context = {
          name: WINDOW.location.pathname,
          // pageload should always start at timeOrigin (and needs to be in s, not ms)
          startTimestamp: browserPerformanceTimeOrigin ? browserPerformanceTimeOrigin / 1000 : undefined,
          origin: 'auto.pageload.browser',
          attributes: _defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'url')
        };
        startBrowserTracingPageLoadSpan(client, context);
      }

      if (options.instrumentNavigation && client.emit && WINDOW.location) {
        addHistoryInstrumentationHandler(function (_ref) {
          var to = _ref.to,
              from = _ref.from;

          /**
           * This early return is there to account for some cases where a navigation transaction starts right after
           * long-running pageload. We make sure that if `from` is undefined and a valid `startingURL` exists, we don't
           * create an uneccessary navigation transaction.
           *
           * This was hard to duplicate, but this behavior stopped as soon as this fix was applied. This issue might also
           * only be caused in certain development environments where the usage of a hot module reloader is causing
           * errors.
           */
          if (from === undefined && startingUrl && startingUrl.indexOf(to) !== -1) {
            startingUrl = undefined;
            return;
          }

          if (from !== to) {
            startingUrl = undefined;
            var _context = {
              name: WINDOW.location.pathname,
              origin: 'auto.navigation.browser',
              attributes: _defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'url')
            };
            startBrowserTracingNavigationSpan(client, _context);
          }
        });
      }

      if (markBackgroundSpan) {
        registerBackgroundTabDetection();
      }

      if (_experiments.enableInteractions) {
        registerInteractionListener(options, latestRoute);
      }

      if (options.enableInp) {
        registerInpInteractionListener(interactionIdToRouteNameMapping, latestRoute);
      }

      instrumentOutgoingRequests({
        traceFetch: traceFetch,
        traceXHR: traceXHR,
        tracePropagationTargets: tracePropagationTargets,
        shouldCreateSpanForRequest: shouldCreateSpanForRequest,
        enableHTTPTimings: enableHTTPTimings
      });
    },
    // TODO v8: Remove this again
    // This is private API that we use to fix converted BrowserTracing integrations in Next.js & SvelteKit
    options: options
  };
};
/**
 * Manually start a page load span.
 * This will only do something if the BrowserTracing integration has been setup.
 */


function startBrowserTracingPageLoadSpan(client, spanOptions) {
  if (!client.emit) {
    return;
  }

  client.emit('startPageLoadSpan', spanOptions);
  var span = getActiveSpan();
  var op = span && spanToJSON(span).op;
  return op === 'pageload' ? span : undefined;
}
/**
 * Manually start a navigation span.
 * This will only do something if the BrowserTracing integration has been setup.
 */


function startBrowserTracingNavigationSpan(client, spanOptions) {
  if (!client.emit) {
    return;
  }

  client.emit('startNavigationSpan', spanOptions);
  var span = getActiveSpan();
  var op = span && spanToJSON(span).op;
  return op === 'navigation' ? span : undefined;
}
/** Returns the value of a meta tag */


function getMetaContent(metaName) {
  // Can't specify generic to `getDomElement` because tracing can be used
  // in a variety of environments, have to disable `no-unsafe-member-access`
  // as a result.
  var metaTag = getDomElement("meta[name=".concat(metaName, "]")); // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access

  return metaTag ? metaTag.getAttribute('content') : undefined;
}
/** Start listener for interaction transactions */


function registerInteractionListener(options, latestRoute) {
  var inflightInteractionTransaction;

  var registerInteractionTransaction = function registerInteractionTransaction() {
    var idleTimeout = options.idleTimeout,
        finalTimeout = options.finalTimeout,
        heartbeatInterval = options.heartbeatInterval;
    var op = 'ui.action.click'; // eslint-disable-next-line deprecation/deprecation

    var currentTransaction = getActiveTransaction();

    if (currentTransaction && currentTransaction.op && ['navigation', 'pageload'].includes(currentTransaction.op)) {
      DEBUG_BUILD && logger.warn("[Tracing] Did not create ".concat(op, " transaction because a pageload or navigation transaction is in progress."));
      return undefined;
    }

    if (inflightInteractionTransaction) {
      inflightInteractionTransaction.setFinishReason('interactionInterrupted');
      inflightInteractionTransaction.end();
      inflightInteractionTransaction = undefined;
    }

    if (!latestRoute.name) {
      DEBUG_BUILD && logger.warn("[Tracing] Did not create ".concat(op, " transaction because _latestRouteName is missing."));
      return undefined;
    }

    var location = WINDOW.location;
    var context = {
      name: latestRoute.name,
      op: op,
      trimEnd: true,
      data: _defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, latestRoute.context ? getSource(latestRoute.context) : 'url')
    };
    inflightInteractionTransaction = startIdleTransaction( // eslint-disable-next-line deprecation/deprecation
    getCurrentHub(), context, idleTimeout, finalTimeout, true, {
      location: location
    }, // for use in the tracesSampler
    heartbeatInterval);
  };

  ['click'].forEach(function (type) {
    if (WINDOW.document) {
      addEventListener(type, registerInteractionTransaction, {
        once: false,
        capture: true
      });
    }
  });
}

function isPerformanceEventTiming(entry) {
  return 'duration' in entry;
}
/** We store up to 10 interaction candidates max to cap memory usage. This is the same cap as getINP from web-vitals */


var MAX_INTERACTIONS = 10;
/** Creates a listener on interaction entries, and maps interactionIds to the origin path of the interaction */

function registerInpInteractionListener(interactionIdToRouteNameMapping, latestRoute) {
  var handleEntries = function handleEntries(_ref2) {
    var entries = _ref2.entries;
    var client = getClient(); // We need to get the replay, user, and activeTransaction from the current scope
    // so that we can associate replay id, profile id, and a user display to the span

    var replay = client !== undefined && client.getIntegrationByName !== undefined ? client.getIntegrationByName('Replay') : undefined;
    var replayId = replay !== undefined ? replay.getReplayId() : undefined; // eslint-disable-next-line deprecation/deprecation

    var activeTransaction = getActiveTransaction();
    var currentScope = getCurrentScope();
    var user = currentScope !== undefined ? currentScope.getUser() : undefined;
    entries.forEach(function (entry) {
      if (isPerformanceEventTiming(entry)) {
        var interactionId = entry.interactionId;

        if (interactionId === undefined) {
          return;
        }

        var existingInteraction = interactionIdToRouteNameMapping[interactionId];
        var duration = entry.duration;
        var startTime = entry.startTime;
        var keys = Object.keys(interactionIdToRouteNameMapping);
        var minInteractionId = keys.length > 0 ? keys.reduce(function (a, b) {
          return interactionIdToRouteNameMapping[a].duration < interactionIdToRouteNameMapping[b].duration ? a : b;
        }) : undefined; // For a first input event to be considered, we must check that an interaction event does not already exist with the same duration and start time.
        // This is also checked in the web-vitals library.

        if (entry.entryType === 'first-input') {
          var matchingEntry = keys.map(function (key) {
            return interactionIdToRouteNameMapping[key];
          }).some(function (interaction) {
            return interaction.duration === duration && interaction.startTime === startTime;
          });

          if (matchingEntry) {
            return;
          }
        } // Interactions with an id of 0 and are not first-input are not valid.


        if (!interactionId) {
          return;
        } // If the interaction already exists, we want to use the duration of the longest entry, since that is what the INP metric uses.


        if (existingInteraction) {
          existingInteraction.duration = Math.max(existingInteraction.duration, duration);
        } else if (keys.length < MAX_INTERACTIONS || minInteractionId === undefined || duration > interactionIdToRouteNameMapping[minInteractionId].duration) {
          // If the interaction does not exist, we want to add it to the mapping if there is space, or if the duration is longer than the shortest entry.
          var routeName = latestRoute.name;
          var parentContext = latestRoute.context;

          if (routeName && parentContext) {
            if (minInteractionId && Object.keys(interactionIdToRouteNameMapping).length >= MAX_INTERACTIONS) {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete interactionIdToRouteNameMapping[minInteractionId];
            }

            interactionIdToRouteNameMapping[interactionId] = {
              routeName: routeName,
              duration: duration,
              parentContext: parentContext,
              user: user,
              activeTransaction: activeTransaction,
              replayId: replayId,
              startTime: startTime
            };
          }
        }
      }
    });
  };

  addPerformanceInstrumentationHandler('event', handleEntries);
  addPerformanceInstrumentationHandler('first-input', handleEntries);
}

function getSource(context) {
  var sourceFromAttributes = context.attributes && context.attributes[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]; // eslint-disable-next-line deprecation/deprecation

  var sourceFromData = context.data && context.data[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]; // eslint-disable-next-line deprecation/deprecation

  var sourceFromMetadata = context.metadata && context.metadata.source;
  return sourceFromAttributes || sourceFromData || sourceFromMetadata;
}

export { BROWSER_TRACING_INTEGRATION_ID, browserTracingIntegration, getMetaContent, startBrowserTracingNavigationSpan, startBrowserTracingPageLoadSpan };